'use client'

/**
 * A company's own look on the admin sign-in page (branded sign-in, 3 Nov 2026; contract
 * docs/superpowers/specs/2026-11-03-branded-sign-in-design.md section 4.5, screens A1, A3 to A8).
 *
 * HumanFirewall is a security-awareness platform; a customer company (example: CSB Bank on csb.humanfirewall.ai)
 * gets its logo and name where the HumanFirewall shield and name stand on today's page, in the same place and size, so
 * the page keeps the product's look. The name and logo come from the API's public "which methods" answer, which never
 * carries an id.
 *
 *   SignInCompanyBrand  the header above the card on a company address (A3 to A8)
 *   SignInCompanyMark   the logo, or a building when the company has none or it fails to load
 *   SignInCompanyNote   dash, once a work e-mail of that company is typed: "CSB Bank signs in with its work accounts" (A1)
 *   SignInCompanyFooter "Only CSB Bank accounts sign in here", or on the company's own domain why there is no passkey
 */

import { useState } from 'react'
import { Building2 } from 'lucide-react'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/lib/i18n/rich-text'
import { cn } from '@/lib/utils'

export interface SignInCompany {
  name: string
  logoUrl: string | null
}

/** The logo on a white plate (a tenant logo is drawn for a white page), or a building in the primary tint. */
export function SignInCompanyMark({ company, size = 'lg' }: { company: SignInCompany; size?: 'lg' | 'sm' }) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState<string | null>(null)
  const box = size === 'lg' ? 'h-12 min-w-12 sm:h-14 sm:min-w-14 rounded-xl' : 'h-7 min-w-10 rounded-md'
  if (company.logoUrl && failed !== company.logoUrl) {
    return (
      <span className={cn('flex items-center justify-center bg-white px-1.5 ring-1 ring-border', box)}>
        {/* eslint-disable-next-line @next/next/no-img-element -- a tenant upload or CDN URL, not a static asset */}
        <img
          data-testid="sign-in-company-logo"
          src={company.logoUrl}
          alt={t('signInPage.brand.logoAlt', { company: company.name })}
          className={cn('w-auto object-contain', size === 'lg' ? 'h-8 max-w-40 sm:h-10' : 'h-5 max-w-24')}
          onError={() => setFailed(company.logoUrl)}
        />
      </span>
    )
  }
  return (
    <span className={cn('flex items-center justify-center bg-primary/10 text-primary', box)}>
      <Building2 className={size === 'lg' ? 'h-7 w-7 sm:h-8 sm:w-8' : 'h-4 w-4'} aria-hidden="true" />
    </span>
  )
}

/** Where today's page has the HumanFirewall shield, name and tagline. */
export function SignInCompanyBrand({ company, className }: { company: SignInCompany; className?: string }) {
  const { t } = useTranslation()
  return (
    <div data-testid="sign-in-company-brand" className={cn('flex flex-col items-center mb-6 sm:mb-8', className)}>
      <div className="mb-3 sm:mb-4">
        <SignInCompanyMark company={company} />
      </div>
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-center">
        <UserText>{company.name}</UserText>
      </h1>
      <p className="text-xs sm:text-sm text-muted-foreground mt-1 text-center px-2">{t('signInPage.brand.tag')}</p>
    </div>
  )
}

/** A1: the card on dash has become this company's. */
export function SignInCompanyNote({ company }: { company: SignInCompany }) {
  const { t } = useTranslation()
  return (
    <div
      data-testid="sign-in-company-note"
      className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground"
    >
      <SignInCompanyMark company={company} size="sm" />
      <span className="min-w-0">
        {richText(t('signInPage.company.note'), {
          company: <b className="font-semibold text-foreground">{company.name}</b>,
        })}
      </span>
    </div>
  )
}

/** Under the card on a company address. */
export function SignInCompanyFooter({ company, ownDomain }: { company: SignInCompany; ownDomain: boolean }) {
  const { t } = useTranslation()
  return (
    <p data-testid="sign-in-company-footer" className="text-sm text-muted-foreground">
      {ownDomain ? t('signInPage.company.noPasskeyHere') : t('signInPage.company.onlyCompany', { company: company.name })}
    </p>
  )
}
